/**
 * Agenda session helpers shared by the schedule, agenda, cue sheet and programme tools. Pure.
 * Sessions are flat records (see types.ts): a day, a 24-hour start, an optional end, plus room and presenter.
 */
import type { Session } from "./types";

export const toMinutes = (t: string | undefined): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t ?? "");
  if (!m) return null;
  const h = Number(m[1]), mi = Number(m[2]);
  return h < 24 && mi < 60 ? h * 60 + mi : null;
};

export const fromMinutes = (total: number): string => {
  const t = ((Math.round(total) % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

/** Chronological order: day, then start, then end, then title. */
export function sortSessions(list: readonly Session[]): Session[] {
  return [...list].sort((a, b) =>
    a.day.localeCompare(b.day) || a.start.localeCompare(b.start) || (a.end ?? "").localeCompare(b.end ?? "") || a.title.localeCompare(b.title));
}

export const daysOf = (list: readonly Session[]): string[] => Array.from(new Set(list.map((s) => s.day))).sort();

export interface Clash { a: Session; b: Session; why: "room" | "presenter" }

/** Sessions on the same day that overlap in time and share a room or a presenter. Back-to-back is not a clash. */
export function findClashes(list: readonly Session[]): Clash[] {
  const out: Clash[] = [];
  const ordered = sortSessions(list);
  const end = (s: Session) => toMinutes(s.end) ?? (toMinutes(s.start) ?? 0) + 1;
  for (let i = 0; i < ordered.length; i++) {
    for (let j = i + 1; j < ordered.length; j++) {
      const a = ordered[i], b = ordered[j];
      if (a.day !== b.day) break;
      const as = toMinutes(a.start), bs = toMinutes(b.start);
      if (as === null || bs === null) continue;
      if (bs >= end(a)) continue; // ordered by start, so later ones start after a ends only when sorted ahead
      const same = (x?: string, y?: string) => !!x && !!y && x.trim().toLowerCase() === y.trim().toLowerCase();
      if (same(a.room, b.room)) out.push({ a, b, why: "room" });
      if (same(a.presenter, b.presenter)) out.push({ a, b, why: "presenter" });
    }
  }
  return out;
}

/** Moves every start and end on one day by a number of minutes (negative = earlier). Times stay inside the day. */
export function shiftDay(list: readonly Session[], day: string, minutes: number): { sessions: Session[]; clamped: number } {
  let clamped = 0;
  const move = (t: string): string => {
    const m = toMinutes(t);
    if (m === null) return t;
    const n = m + minutes;
    if (n < 0 || n > 1439) { clamped++; return fromMinutes(Math.min(1439, Math.max(0, n))); }
    return fromMinutes(n);
  };
  return {
    sessions: list.map((s) => (s.day === day ? { ...s, start: move(s.start), ...(s.end ? { end: move(s.end) } : {}) } : s)),
    clamped,
  };
}

/** Length in minutes, or null when there is no end time or it is not after the start. */
export function durationMinutes(s: Session): number | null {
  const a = toMinutes(s.start), b = toMinutes(s.end);
  return a !== null && b !== null && b > a ? b - a : null;
}

export const formatDuration = (min: number): string => (min < 60 ? `${min} min` : `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ""}`);

/** Plain-text schedule for one day (or every day), grouped by day when more than one. */
export function scheduleText(list: readonly Session[], title: string, dayLabel: (d: string) => string): string {
  const days = daysOf(list);
  const lines: string[] = [title];
  for (const day of days) {
    lines.push("", days.length > 1 ? dayLabel(day) : dayLabel(day).trim());
    for (const s of sortSessions(list.filter((x) => x.day === day))) {
      const when = s.end ? `${s.start}-${s.end}` : s.start;
      const extra = [s.room, s.presenter].filter(Boolean).join(", ");
      lines.push(`${when}  ${s.title}${extra ? ` (${extra})` : ""}`);
    }
  }
  return lines.join("\n");
}

/** Brings the old single-day schedule (time + activity rows) into sessions on one day. */
export function migrateLegacySlots(raw: unknown, day: string, makeId: () => string): Session[] {
  if (!Array.isArray(raw)) return [];
  const out: Session[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const start = typeof o.time === "string" ? o.time : "";
    const title = typeof o.activity === "string" ? o.activity.trim() : "";
    if (toMinutes(start) === null || !title) continue;
    out.push({ id: makeId(), title: title.slice(0, 300), day, start });
  }
  return out;
}
